import * as Sentry from '@sentry/react';
import type { StytchB2BClient } from '@stytch/react/b2b';

import getSolStandardError from '@/error/sol-standard-errors';
import SolAPIError from '@/error/SolAPIError';

import {
  type HttpHeaders,
  type HttpHeadersMap,
  type HttpMethod,
  type HttpResponse,
  HttpRequestError,
  isAbortError,
  isHttpRequestError,
} from './httpType';
import { type UserProfileType } from './user';

/** Matches current `requestSol` usage: GraphQL-style JSON objects or binary uploads (File extends Blob). */
export type SolRequestBody = Blob | Record<string, unknown>;

type IRequest<TData extends SolRequestBody | undefined = undefined> = {
  data?: TData;
  headers?: HttpHeaders;
  method?: HttpMethod;
  signal?: AbortSignal;
  url: string;
};

const API_HOST =
  process.env.NODE_ENV === 'development' ? (window as Window).API_HOST : document.location.origin;

// localhost calls to `sol` go to http://localhost:3000/sol/* and we rewrite the path in Vite's
// proxy to route to the proper url. For deployed environments, we go directly to Sol's host.
export const SOL_API_HOST =
  window.ENV === 'local' ? `${API_HOST}/sol/` : import.meta.env.VITE_SOL_HOST;

const jsonContentType = 'application/json';

const headersToObject = (headers: Headers): HttpHeadersMap => {
  const mapped: HttpHeadersMap = {};
  headers.forEach((value, key) => {
    mapped[key.toLowerCase()] = value;
  });

  return mapped;
};

class Fetcher {
  private userData?: UserProfileType = undefined;
  private stytch?: StytchB2BClient;
  private logOut?: () => void;

  private getAuthHeaders = (): Record<string, string> => {
    const jwtToken = this.stytch?.session.getTokens()?.session_jwt;

    if (!jwtToken) {
      return {};
    }

    const headers: Record<string, string> = {
      authorization: `Bearer ${jwtToken}`,
    };

    const instanceId = this.userData?.currentOrg.instanceId;
    if (instanceId) {
      headers['x-instance-id'] = instanceId;
    }

    return headers;
  };

  private parseResponseBody = async (response: Response): Promise<unknown> => {
    if (response.status === 204 || response.status === 205) {
      return undefined;
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

    if (contentType.includes(jsonContentType)) {
      try {
        return await response.json();
      } catch {
        return undefined;
      }
    }

    const text = await response.text();
    return text === '' ? undefined : text;
  };

  private normalizeHeaders = (headers?: HttpHeaders): Record<string, string> => {
    if (!headers) {
      return {};
    }

    return Object.entries(headers).reduce<Record<string, string>>((memo, [key, value]) => {
      if (value == null) {
        return memo;
      }

      memo[key.toLowerCase()] = String(value);
      return memo;
    }, {});
  };

  private buildRequestPayload = <TData extends SolRequestBody | undefined>({
    data,
    headers,
    method = 'GET',
  }: Pick<IRequest<TData>, 'data' | 'headers' | 'method'>) => {
    const requestMethod = method.toUpperCase() as HttpMethod;

    const requestHeaders: Record<string, string> = {
      ...this.normalizeHeaders(headers),
      ...this.getAuthHeaders(),
    };

    let body: BodyInit | undefined = undefined;

    if (data != null && requestMethod !== 'GET' && requestMethod !== 'HEAD') {
      if (data instanceof Blob) {
        body = data;
      } else {
        body = JSON.stringify(data);

        if (requestHeaders['content-type'] == null) {
          requestHeaders['content-type'] = jsonContentType;
        }
      }
    }

    return {
      body,
      method: requestMethod,
      requestHeaders,
    };
  };

  /** Maps HTTP failures to `SolAPIError` when the body parses as a Sol error payload. */
  private rethrowAfterLogging(error: unknown): never {
    if (isAbortError(error)) {
      throw error;
    }

    Sentry.captureException(error);

    if (isHttpRequestError(error)) {
      if (error.status === 401) {
        this.logOut?.();
      }

      if (error.data) {
        const solError = getSolStandardError(error);
        const requestId = error.headers['x-request-id'];
        const standardizedError = new SolAPIError(requestId, [solError]);
        console.error(standardizedError.toString());
        throw standardizedError;
      }
    }

    console.error(error);
    throw error;
  }

  private request = async <TData extends SolRequestBody | undefined, TResponse = unknown>(
    url: string,
    { signal, ...rest }: Omit<IRequest<TData>, 'url'>,
  ): Promise<HttpResponse<TResponse>> => {
    const { body, method, requestHeaders } = this.buildRequestPayload(rest);

    try {
      const response = await fetch(url, {
        body,
        headers: requestHeaders,
        method,
        signal,
      });

      const responseHeaders = headersToObject(response.headers);
      const responseData = await this.parseResponseBody(response);

      if (!response.ok) {
        throw new HttpRequestError(`Request failed with status ${response.status}`, {
          data: responseData,
          headers: responseHeaders,
          status: response.status,
        });
      }

      return {
        data: responseData as TResponse,
        headers: responseHeaders,
        status: response.status,
      };
    } catch (error) {
      this.rethrowAfterLogging(error);
    }
  };

  setUserData = (
    userData: UserProfileType | undefined,
    stytch: StytchB2BClient,
    logOut: () => void,
  ) => {
    this.userData = userData;
    this.stytch = stytch;
    this.logOut = logOut;
  };

  getStytch = () => {
    return this.stytch;
  };

  requestSol = <TData extends SolRequestBody | undefined = undefined, TResponse = unknown>({
    url,
    ...rest
  }: IRequest<TData>): Promise<HttpResponse<TResponse>> =>
    this.request(`${SOL_API_HOST}${url}`, rest);
}

export default new Fetcher();
